import {Dropdown} from '@/components-v2/molecules/Dropdown/Dropdown'
import {SegmentModal} from '@/features/Segments'
import {SegmentTypes} from '@/utils/constants/promotionConstants'
import {useMemo, useState} from 'react'

/**
 * @typedef {Object} Segment
 * @property {string} segmentId Segment identifier.
 * @property {string} segmentName Segment display name.
 */

/**
 * @typedef {Object} SegmentDropdownProps
 * @property {Segment[]} [value] Selected segments.
 * @property {(value: Segment[]) => void} [onChange] Callback triggered with updated selected segments.
 * @property {boolean} [disabled] Whether the dropdown is disabled.
 */

/**
 * @param {SegmentDropdownProps} props
 */
export const SegmentDropdown = ({value = [], onChange = () => {}, disabled = false}) => {
  const [showSegmentModal, setShowSegmentModal] = useState(false)

  const normalizedValue = value.map(segment =>
    typeof segment === 'string' ? {segmentId: segment, segmentName: segment} : segment
  )

  const handleSubmit = values => {
    onChange(values.map(({segmentId, segmentName}) => ({segmentId, segmentName})))
  }

  const handleClose = () => {
    setShowSegmentModal(false)
  }

  const existingSelections = useMemo(() => {
    return normalizedValue.map(segment => ({
      segmentId: segment.segmentId,
      segmentGroupId: '',
      segmentName: segment.segmentName,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(value || []).map(s => (typeof s === 'string' ? s : s.segmentId)).join('|')])

  const dropdownValue = normalizedValue.map(segment => ({id: segment.segmentId, displayText: segment.segmentName}))

  const placeholderText =
    normalizedValue.length > 0 ? normalizedValue.map(s => s.segmentName).join(', ') : 'Select segments'

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setShowSegmentModal(true)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setShowSegmentModal(true)
          }
        }}
        style={{cursor: 'pointer'}}
      >
        <Dropdown
          className="text-sm"
          childList={[]}
          selectedValue={dropdownValue}
          isJsonWrap={false}
          selection={'multiple'}
          placeholder={placeholderText || ''}
          updateValue={() => {}}
          style={{pointerEvents: 'none', width: 'auto'}}
          disabled={disabled}
        />
      </div>
      <SegmentModal
        show={showSegmentModal}
        type={SegmentTypes.SEGMENT_LIST}
        onClose={handleClose}
        onSubmit={handleSubmit}
        existingSelections={existingSelections}
      />
    </div>
  )
}
